import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  glossaryEntriesSlice,
  GlossaryRow,
  GlossaryEntriesState,
} from '@/states/slices/glossaryEntriesSlice';
import { AppDispatch, RootState, useAppDispatch } from '@/states/store';

/**
 * 用語集エントリを Redux で管理するカスタムフック。
 *
 * 永続化は redux-persist が担うため、このフックは直接 localStorage に触らない。
 *
 * ### 既知の制約（この PR では対処しない）
 * localStorage の容量を超えた場合、redux-persist は書き込み失敗を握りつぶすため
 * ユーザーには何も表示されない。1 言語 1000 件 × 44 言語で理論上は上限に届くが、
 * 実運用では起きにくいため現時点では対処していない。
 * 対処する場合は、登録前に試し書きして容量を確認するか、persistConfig に
 * writeFailHandler を設定する。
 */
export const useGlossaryEntries = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const { setGlossaryEntries, clearGlossaryEntries } =
    glossaryEntriesSlice.actions;

  const { entries } = useSelector<RootState, GlossaryEntriesState>(
    (state) => state.glossaryEntries,
  );

  /**
   * 用語集を全件置き換え
   */
  const changeGlossaryEntries = useCallback(
    (value: GlossaryRow[]) => {
      dispatch(setGlossaryEntries(value));
    },
    [dispatch, setGlossaryEntries],
  );

  /**
   * 用語集に関する全てのStateをリセット
   */
  const resetState = useCallback(() => {
    dispatch(clearGlossaryEntries());
  }, [clearGlossaryEntries, dispatch]);

  return {
    entries,
    setGlossaryEntries: changeGlossaryEntries,
    resetState,
  };
};
